import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Zap } from "lucide-react"; // For logo accent

export default function IntroScreen({ onContinue }) {
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    // Skip if already unlocked
    if (localStorage.getItem("audioUnlocked") === "true") {
      setVisible(false);
      onContinue();
    }
  }, [onContinue]);

  const handleContinue = () => {
    localStorage.setItem("audioUnlocked", "true");
    onContinue();
    setVisible(false);
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.6 }}
          className="fixed inset-0 z-[9999] flex items-center justify-center overflow-hidden"
        >
          {/* Animated gradient background */}
          <div className="absolute inset-0 bg-gradient-to-br from-orange-500 via-pink-500 to-purple-600 animate-gradient-x" />

          {/* Glowing particles */}
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute w-64 h-64 bg-orange-400 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-pulse-slow top-10 left-10"></div>
            <div className="absolute w-72 h-72 bg-pink-400 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-pulse-slow bottom-10 right-10"></div>
          </div>

          {/* Content */}
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: "spring", stiffness: 80, damping: 15 }}
            className="relative flex flex-col items-center text-center space-y-6"
          >
            {/* Logo */}
            <motion.div
              className="flex items-center justify-center w-28 h-28 rounded-full bg-white shadow-2xl relative"
              initial={{ y: -40, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2, type: "spring", stiffness: 80 }}
            >
              <Zap className="w-14 h-14 text-orange-500 drop-shadow-lg" />
            </motion.div>

            {/* App Name */}
            <motion.h1
              className="text-5xl font-extrabold tracking-tight text-white drop-shadow-2xl"
              initial={{ y: -20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.3 }}
            >
              Packky
            </motion.h1>

            {/* Tagline */}
            <motion.p
              className="text-lg text-white/80 max-w-md"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
            >
              Delivering speed, security, and brilliance — <br />
              Welcome to the next-gen logistics dashboard.
            </motion.p>

            {/* Click to Continue */}
            <motion.button
              onClick={handleContinue}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="px-10 py-4 bg-white text-orange-600 font-bold text-lg rounded-full shadow-xl transition-all animate-pulse hover:bg-orange-50 hover:shadow-2xl"
            >
              Click to Continue
            </motion.button>

            {/* Footer */}
            <motion.span
              className="text-white/50 text-sm mt-6"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.8 }}
            >
              © {new Date().getFullYear()} Packky. All Rights Reserved.
            </motion.span>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
